import React from "react";
import { Alert } from 'react-bootstrap';
import ReactLoading from 'react-loading';
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import { IAppState } from "../../state/interfaces";
import { connect } from "react-redux";
import { FULL_QUOTE_STEPS, COMPONENTS, QUOTE_QUESTIONS } from "../../AppConstants";
import { ValidatableForm } from "../../components/ValidatableForm";
import Utilities from "../../classes/Utilities";
import { validationUtils } from "../../validation/Validation";
import { QuoteStep } from "./QuoteStepLayout";
import ButtonContinue from "../../components/ButtonContinue";
import QuoteStepBase from '../../components/QuoteStepBase';
import { IQuoteApiClient, ILOBApiClient, IPaths } from "../../classes/Domain";
import { IBaseResult, IExternalQuote, IQuoteQuestion } from "../../classes/ExternalQuote";
import QuoteActions from "../../state/appActions";
import FullQuoteActions from "../../state/fullQuoteActions";
import Question from '../../components/Question';
import { IEsignatureResponse } from "./Finalize";

interface IAppStateUpdate {
    quoteId: number,
    questions: Array<IQuoteQuestion>;
}

interface IState extends IQuoteStepState {
    loadingQuestions: boolean,
    updateResult: IBaseResult | null,
    questions: Array<IQuoteQuestion> | null;
}

interface IProps extends IQuoteStepComponentProps {
    externalQuote: IExternalQuote,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths,
    getESignatureForms: (update: any) => Promise<any>;
}

class Questions extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);

        this.state = {
            loadingQuestions: false,
            updateResult: null,
            questions: null,
            updatingStore: false,
            errors: null
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        this.setState({ ...this.state, updatingStore: false, errors: null });

        validationUtils.subscribeToValidationFailure(FULL_QUOTE_STEPS.QUESTIONS,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, errors: validationData });
            });

        if (this.props.externalQuote.questions && this.props.externalQuote.questions.length > 0) {
            this.setState({ ...this.state, loadingQuestions:false, questions: this.props.externalQuote.questions });
        }
        else {
            const questionsApiClient = this.props.lobApiClient;
            this.setState({ loadingQuestions: true }, () => {
                questionsApiClient
                    .getQuoteQuestions(this.props.externalQuote?.rtrQuoteId)
                    .then((response) => {
                        this.setState({ ...this.state, loadingQuestions: false, questions: response.data });
                    });
            });
        }
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(FULL_QUOTE_STEPS.QUESTIONS);
    }


    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {
            this.props.updateAppState({
                quoteId: this.props.externalQuote?.rtrQuoteId,
                questions: this.state.questions
            })
            .then((response) => {
                this.setState({ updateResult: response.data }, () => {
                    if (!this.state.updateResult?.isSuccess) {
                        this.setState({ ...this.state, updatingStore: false, errors: null });
                    }
                    else {
                        this.props.getESignatureForms({
                            quoteId: this.props.externalQuote?.rtrQuoteId,
                        })
                        .then((esigResponse: IEsignatureResponse) => {
                            this.setState({ updatingStore: false }, () => {
                                if (esigResponse.data.esignatureForms && 0 < esigResponse.data.esignatureForms.length) {
                                    this.props.history.push(Utilities.formatUrl(this.props.PATHS.FQ_ESIGNATURE, ""));
                                }
                                else {
                                    this.props.history.push(this.props.PATHS.FQ_PAYMENT);
                                }
                            });
                        },
                        () => {
                            this.setState({ ...this.state, updatingStore: false, errors: null });
                        });
                    }
                });
            }, () => this.setState({ ...this.state, updatingStore: false }));
        });
    }

    render() {
        return (
            <QuoteStep id={this.props.id}>
                <ValidatableForm hasErrors={null != this.state.errors}>
                    <div className="titleArea">
                        <h5>Questions</h5>
                    </div>

                    {
                        this.state.loadingQuestions
                            ? <div className={"w-100 loading-indicator"}><ReactLoading type={"spin"} color={"#0096AE"} height={'40px'} width={'40px'} /></div>
                            : null
                    }

                    {
                        this.state.updateResult && !this.state.updateResult.isSuccess && this.state.updateResult.errors
                            ? <Alert variant={"danger"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review errors below</p>
                                <ul>
                                    {
                                        this.state.updateResult.errors.map((error) => (
                                            <li key={Utilities.uniqueId()}>
                                                <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </Alert>
                            : null
                    }

                    <p className="info">We need to gather some final questions:</p>

                    {
                        this.state.questions?.map((question, i) => (
                            <Question
                                key={Utilities.uniqueId()}
                                name={question.code}
                                selected={(v) => { this.setStateValue(v === QUOTE_QUESTIONS.YES_NO.YES ? true : false, "answer", question) }}
                                selectedValue={question.answer !== null ? (question.answer ? QUOTE_QUESTIONS.YES_NO.YES : QUOTE_QUESTIONS.YES_NO.NO) : ''}
                                error={validationUtils.getValidationErrorFor(this.state.errors, ("answer_" + question.code))}
                                items={[
                                    QUOTE_QUESTIONS.YES_NO.YES,
                                    QUOTE_QUESTIONS.YES_NO.NO
                                ]}
                                lable={(i + 1) + '. ' + question.question}
                            />
                        ))
                    }

                    <div className="btnContainer">
                        <ButtonContinue
                            disabled={this.state.updatingStore}
                            showLoading={this.state.updatingStore}
                            onClick={this.next} />
                    </div>
                </ValidatableForm>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        externalQuote: state.externalQuote,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.POLICY_DETAILS)),
        updateAppState: (update: IAppStateUpdate) => dispatch(fullQuoteActions.updateQuestions(update.quoteId, update.questions)),
        getESignatureForms: (update: IAppStateUpdate) => dispatch(fullQuoteActions.getESignatureForms(update.quoteId))
    };
};

export default connect(mapProps, mapDispatch)(Questions);